import type { StaticAutocompleteOption } from 'components/technical/inputs/Autocomplete/StaticSingleAutocomplete.props';
import { HEIGHT_PX, SubAccountLabel } from './SubAccountLabel.tsx';
import { type IAccount, IAssetType, type ISubAccount } from '../../../generated/graphql';
import { IconVariant } from '../../market/asset/cryptocurrencies/CryptocurrenciesData';
import { GENIE_VENUE, venues } from '../../venue/VenueData.tsx';
import { AccountLabel } from './AccountLabel.tsx';
import isNil from 'lodash/fp/isNil';
import { sortBy } from 'lodash/fp';
import { logWarnOnce } from '../../log.utils.ts';
import type { AssetSelectOptionValue } from '../../market/asset/AssetService.tsx';

type SubAccount = Pick<ISubAccount, 'id' | 'name'>;
type SubAccountWithSub = SubAccount & { subAccounts: SubAccount[] };
type Account = Pick<IAccount, 'venue' | 'name' | 'id'>;

export type CreateAccountIdAutocompleteOptionsInputAccount = Account;
export type CreateSubAccountIdAutocompleteOptionsInputAccount = Account & {
  subAccounts: SubAccount[];
};

export type SubAccountOptionValue<TSubAccount extends SubAccount> = TSubAccount & {
  account: Omit<Account, 'subAccounts'>;
};

export type AccountOptionValue<TAccount extends Account> = TAccount;

export const createAccountAutocompleteOptions = <TAccount extends Account>(
  accounts: Array<TAccount>,
  { ignoreData }: { ignoreData?: { accounts: Set<string>; subAccounts: Set<string> } } = {}
): {
  options: StaticAutocompleteOption<AccountOptionValue<TAccount>>[];
  optionHeight: number;
  limitTags: number;
  isValueEqual: (
    a: AccountOptionValue<TAccount> | undefined | null,
    b: AccountOptionValue<TAccount> | undefined | null
  ) => boolean;
} => {
  const hasIgnoreData = Object.keys(ignoreData ?? {}).length > 0;
  const responseObject = {
    optionHeight: HEIGHT_PX,
    limitTags: 1,
    isValueEqual: (
      a: AccountOptionValue<TAccount> | undefined | null,
      b: AccountOptionValue<TAccount> | undefined | null
    ): boolean => (!a && !b) || a?.id === b?.id,
  };
  const options = sortBy((acc) => acc.name, accounts).map((account) => ({
    label: <AccountLabel account={account} size={IconVariant.MEDIUM} wrap={false} plain />,
    value: account,
    searchText: account.name,
    key: account.id,
  }));

  if (!hasIgnoreData) {
    return {
      ...responseObject,
      options,
    };
  }

  const filteredOptions = options.filter((option) => {
    const value = option.value as unknown as SubAccountWithSub;
    // Filter out if account ID is in ignoreData.accounts
    if (ignoreData?.accounts?.has(value.id)) {
      return false;
    }

    // Filter out if any subAccount ID is in ignoreData.subAccounts
    if (ignoreData?.subAccounts && value.subAccounts) {
      const hasIgnoredSubAccount = value.subAccounts.some((subAccount) => ignoreData.subAccounts?.has(subAccount.id));
      if (hasIgnoredSubAccount) {
        return false;
      }
    }

    return true;
  });

  return {
    options: filteredOptions,
    ...responseObject,
  };
};

const groupByAccount = (subAccount: { account: { name: string } } | null | undefined): string => {
  return !isNil(subAccount) ? subAccount.account?.name : '';
};

export const createSubAccountAutocompleteOptions = <TSubAccount extends SubAccount>(
  accounts: Array<
    Account & {
      subAccounts: TSubAccount[];
    }
  >,
  { ignoreData }: { ignoreData?: { accounts: Set<string>; subAccounts: Set<string> } } = {}
): {
  options: StaticAutocompleteOption<SubAccountOptionValue<TSubAccount & { account: Account }>>[];
  optionHeight: number;
  limitTags: number;
  groupBy: (option: StaticAutocompleteOption<(TSubAccount & { account: Account }) | undefined>) => string;
  isValueEqual: (
    a: SubAccountOptionValue<TSubAccount> | undefined | null,
    b: SubAccountOptionValue<TSubAccount> | undefined | null
  ) => boolean;
} => {
  const hasIgnoreData = Object.keys(ignoreData ?? {}).length > 0;
  const responseObject = {
    optionHeight: HEIGHT_PX,
    limitTags: 1,
    groupBy: (option: StaticAutocompleteOption<(TSubAccount & { account: Account }) | undefined>): string =>
      groupByAccount(option.value),
    isValueEqual: (
      a: SubAccountOptionValue<TSubAccount> | undefined | null,
      b: SubAccountOptionValue<TSubAccount> | undefined | null
    ): boolean => (!a && !b) || a?.id === b?.id,
  };

  const options = Object.values(accounts).flatMap((account) =>
    account.subAccounts.map((subAccount) => ({
      label: <SubAccountLabel subAccount={{ ...subAccount, account }} size={IconVariant.MEDIUM} wrap={false} plain />,
      value: {
        ...subAccount,
        account: {
          ...account,
          subAccounts: undefined, // better to avoid circle references
        },
      },
      searchText: subAccount.name,
      key: subAccount.id,
    }))
  );

  if (!hasIgnoreData) {
    return {
      ...responseObject,
      // biome-ignore lint/suspicious/noExplicitAny:
      options: sortBy((opt) => groupByAccount(opt.value), options) as any,
    };
  }

  const filteredOptions = options.filter((option) => {
    // Filter out if subAccount ID is in ignoreData.subAccounts
    if (ignoreData?.subAccounts?.has(option.value.id)) {
      return false;
    }

    // Filter out if parent account ID is in ignoreData.accounts
    if (ignoreData?.accounts?.has(option.value.account.id)) {
      return false;
    }

    return true;
  });

  return {
    // biome-ignore lint/suspicious/noExplicitAny:
    options: sortBy((opt) => groupByAccount(opt.value), filteredOptions) as any,
    ...responseObject,
  };
};

export const createSubAccountIdAutocompleteOptions = (
  accounts: CreateSubAccountIdAutocompleteOptionsInputAccount[]
): {
  options: StaticAutocompleteOption<string>[];
  optionHeight: number;
  limitTags: number;
  groupBy: (option: StaticAutocompleteOption<string | null>) => string;
  isValueEqual: (a: string | undefined | null, b: string | undefined | null) => boolean;
} => {
  const props = createSubAccountAutocompleteOptions(accounts);
  const idToAccountName = Object.fromEntries(
    accounts.flatMap((account) => account.subAccounts.map((subAccount) => [subAccount.id, account.name]))
  );
  return {
    ...props,
    groupBy: (option: StaticAutocompleteOption<string | null>): string =>
      !isNil(option.value) ? idToAccountName[option.value] : '',
    options: props.options.map((option) => ({
      ...option,
      value: option.value.id,
    })),
    isValueEqual: (a: string | undefined | null, b: string | undefined | null): boolean => (!a && !b) || a === b,
  };
};

export const createAccountIdAutocompleteOptions = (
  accounts: CreateAccountIdAutocompleteOptionsInputAccount[]
): {
  options: StaticAutocompleteOption<string>[];
  optionHeight: number;
  limitTags: number;
  isValueEqual: (a: string | undefined | null, b: string | undefined | null) => boolean;
} => {
  const props = createAccountAutocompleteOptions(accounts);
  const optionsWithIdAsValue = props.options.map((option) => ({
    ...option,
    value: option.value.id,
  }));
  return {
    ...props,
    options: optionsWithIdAsValue,
    isValueEqual: (a: string | undefined | null, b: string | undefined | null): boolean => (!a && !b) || a === b,
  };
};

export enum AccountType {
  Blockchain = 'BLOCKCHAIN',
  Custodian = 'CUSTODIAN',
  Virtual = 'VIRTUAL',
}

export const calculateAccountType = (account: { venue: { label: string } }): AccountType => {
  const venueLabel = account.venue.label;
  if (venueLabel === GENIE_VENUE) {
    return AccountType.Virtual;
  }

  const venue = venues[venueLabel];
  if (venue?.type === 'blockchain') {
    return AccountType.Blockchain;
  }

  return AccountType.Custodian;
};

export const isAssetValidForAccount = (
  asset: AssetSelectOptionValue,
  account: { venue: { label: string } }
): boolean => {
  const accountType = calculateAccountType(account);
  if (accountType === AccountType.Virtual) {
    return true;
  }

  if ([AccountType.Custodian, AccountType.Blockchain].includes(accountType)) {
    if (!asset.type) {
      logWarnOnce('Unknown asset type for asset', asset);
      return false;
    }

    if ([IAssetType.Public, IAssetType.Private].includes(asset.type)) {
      return true;
    }

    const venue = getExchangeDerivativeAssetVenue(account);
    if (asset.type === IAssetType.Exchange) {
      return venue === asset.exchangeDetails;
    }
    if (asset.type === IAssetType.Derivative) {
      if (!asset.derivativeDetails) {
        logWarnOnce('Unknown derivative details of asset', asset);
        return false;
      }
      return venue === asset.derivativeDetails.exchange;
    }

    console.warn('Unhandled asset type', asset);
    return false;
  }
  console.error('Unknown account type', accountType);
  throw new Error('Unknown account type');
};

// assets should be assigned always to venue for exchange v1
export const getExchangeDerivativeAssetVenue = (
  account: { venue?: { label?: string | null } | null } | null | undefined
): string | undefined => account?.venue?.label?.replace(/-v2$/, '');
